import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import APIRequest from '../api/APIRequest';

const apiRequest = new APIRequest();

export const fetchDevices = createAsyncThunk('biometric/fetchDevices', async (token, { rejectWithValue }) => {
    try {
        const response = await apiRequest.request('/admin/biometric/devices', 'GET', null, token);
        if (response.success) {
            return response.devices;
        }
        return rejectWithValue(response.message);
    } catch (error) {
        return rejectWithValue(error.message);
    }
});

export const addDevice = createAsyncThunk('biometric/addDevice', async ({ deviceData, token }, { rejectWithValue }) => {
    try {
        const response = await apiRequest.request('/admin/biometric/device', 'POST', deviceData, token);
        if (response.success) {
            return response.device;
        }
        return rejectWithValue(response.message);
    } catch (error) {
        return rejectWithValue(error.message);
    }
});

export const updateDevice = createAsyncThunk('biometric/updateDevice', async ({ id, deviceData, token }, { rejectWithValue }) => {
    try {
        const response = await apiRequest.request(`/admin/biometric/device/${id}`, 'PUT', deviceData, token);
        if (response.success) {
            return response.device;
        }
        return rejectWithValue(response.message);
    } catch (error) {
        return rejectWithValue(error.message);
    }
});

export const deleteDevice = createAsyncThunk('biometric/deleteDevice', async ({ id, token }, { rejectWithValue }) => {
    try {
        const response = await apiRequest.request(`/admin/biometric/device/${id}`, 'DELETE', null, token);
        if (response.success) {
            return id;
        }
        return rejectWithValue(response.message);
    } catch (error) {
        return rejectWithValue(error.message);
    }
});

export const testConnection = createAsyncThunk('biometric/testConnection', async ({ deviceData, token }, { rejectWithValue }) => {
    try {
        const response = await apiRequest.request('/admin/biometric/test-connection', 'POST', deviceData, token);
        if (response.success) {
            return response.message;
        }
        return rejectWithValue(response.message);
    } catch (error) {
        return rejectWithValue(error.message || 'Connection failed');
    }
});

export const fetchLogs = createAsyncThunk('biometric/fetchLogs', async (token, { rejectWithValue }) => {
    try {
        const response = await apiRequest.request('/admin/biometric/logs', 'GET', null, token);
        if (response.success) {
            return response.logs;
        }
        return rejectWithValue(response.message);
    } catch (error) {
        return rejectWithValue(error.message);
    }
});

export const manualSync = createAsyncThunk('biometric/manualSync', async (token, { rejectWithValue }) => {
    try {
        const response = await apiRequest.request('/admin/biometric/manual-sync', 'POST', null, token);
        if (response.success) {
            return response.message;
        }
        return rejectWithValue(response.message);
    } catch (error) {
        return rejectWithValue(error.message);
    }
});

const biometricSlice = createSlice({
    name: 'biometric',
    initialState: {
        devices: [],
        logs: [],
        loading: false,
        error: null,
        successMessage: null,
        connectionTestLoading: false,
        connectionTestMessage: null,
        connectionTestError: null
    },
    reducers: {
        clearMessages: (state) => {
            state.error = null;
            state.successMessage = null;
            state.connectionTestMessage = null;
            state.connectionTestError = null;
        }
    },
    extraReducers: (builder) => {
        builder
            // Fetch Devices
            .addCase(fetchDevices.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchDevices.fulfilled, (state, action) => {
                state.loading = false;
                state.devices = action.payload;
            })
            .addCase(fetchDevices.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            
            // Add Device
            .addCase(addDevice.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(addDevice.fulfilled, (state, action) => {
                state.loading = false;
                state.devices.unshift(action.payload);
                state.successMessage = 'Device added successfully';
            })
            .addCase(addDevice.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            
            // Update Device
            .addCase(updateDevice.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(updateDevice.fulfilled, (state, action) => {
                state.loading = false;
                const index = state.devices.findIndex(d => d._id === action.payload._id);
                if (index !== -1) {
                    state.devices[index] = action.payload;
                }
                state.successMessage = 'Device updated successfully';
            })
            .addCase(updateDevice.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            
            // Delete Device
            .addCase(deleteDevice.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(deleteDevice.fulfilled, (state, action) => {
                state.loading = false;
                state.devices = state.devices.filter(d => d._id !== action.payload);
                state.successMessage = 'Device deleted successfully';
            })
            .addCase(deleteDevice.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            
            // Test Connection
            .addCase(testConnection.pending, (state) => {
                state.connectionTestLoading = true;
                state.connectionTestError = null;
                state.connectionTestMessage = null;
            })
            .addCase(testConnection.fulfilled, (state, action) => {
                state.connectionTestLoading = false;
                state.connectionTestMessage = action.payload;
            })
            .addCase(testConnection.rejected, (state, action) => {
                state.connectionTestLoading = false;
                state.connectionTestError = action.payload;
            })
            
            // Fetch Logs
            .addCase(fetchLogs.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchLogs.fulfilled, (state, action) => {
                state.loading = false;
                state.logs = action.payload;
            })
            .addCase(fetchLogs.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            
            // Manual Sync
            .addCase(manualSync.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(manualSync.fulfilled, (state, action) => {
                state.loading = false;
                state.successMessage = action.payload;
            })
            .addCase(manualSync.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            });
    }
});

export const { clearMessages } = biometricSlice.actions;
export default biometricSlice.reducer;
